// src/pages/PlatformTrainingEditor.jsx
import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "../components/Card";
import Button from "../components/Button";
// If your Input.jsx uses `export function Input(...)`, import named:
import { Input } from "../components/Input";
import {
  platformGetTraining,
  platformUpdateTraining,
  platformReplaceContent,
  platformAppendStep,
  platformInsertStep,
  platformRemoveStep,
  platformReorderSteps,
} from "../services/api";
import { loadSession } from "../auth";
import { useTranslation, Trans } from "react-i18next";
import SigningConfigPanel from "../components/SigningConfigPanel";

/* Utils */
async function fileToBase64(file) {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function StepBadge({ type }) {
  const base = "px-2 py-0.5 rounded-full text-xs border";
  const map = {
    video: `${base} bg-blue-50 border-blue-200 text-blue-800`,
    doc: `${base} bg-purple-50 border-purple-200 text-purple-800`,
    quiz: `${base} bg-emerald-50 border-emerald-200 text-emerald-800`,
  };
  const { t } = useTranslation();
  return <span className={map[type] || base}>{t(`platform_editor.type_${type}`)}</span>;
}

export default function PlatformTrainingEditor() {
  const { id } = useParams(); // trainingId
  const nav = useNavigate();
  const user = loadSession()?.user;
  const { t } = useTranslation();

  const isPlatformAdmin =
    user?.derived?.isPlatformAdmin ||
    (Array.isArray(user?.roles) && user.roles.includes("platform_admin"));

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [tdata, setTdata] = useState(null);

  // Metadata form (includes NEW: ssmRequired, suRequired)
  const [meta, setMeta] = useState({
    title: "",
    description: "",
    slug: "",
    visibility: false,
    tenantId: null,
    ssmRequired: false,
    suRequired: false,
  });
  const [savingMeta, setSavingMeta] = useState(false);

  // Steps (server truth) + local editable order
  const stepsFromServer = useMemo(() => tdata?.steps || [], [tdata]);
  const [localSteps, setLocalSteps] = useState([]);
  const [dirtyOrder, setDirtyOrder] = useState(false);
  const savingOrderRef = useRef(false);

  // New step form (append/insert)
  const emptyStep = { type: "doc", title: "", file: null, filename: "", questions: [] };
  const [newStep, setNewStep] = useState(emptyStep);
  const [insertAt, setInsertAt] = useState(""); // ordinal
  const [working, setWorking] = useState(false);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const data = await platformGetTraining(id, 60);
      setTdata(data);
      setMeta({
        title: data?.title || "",
        description: data?.description || "",
        slug: data?.slug || "",
        visibility: !!data?.visibility,
        tenantId: data?.tenantId ?? null,
        ssmRequired: !!data?.ssmRequired, // NEW
        suRequired: !!data?.suRequired,   // NEW
      });
    } catch (e) {
      setErr(e?.message || t("platform_editor.error_generic"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isPlatformAdmin) {
      setLoading(false);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isPlatformAdmin]);

  // whenever server steps change, reset local order + dirty flag
  useEffect(() => {
    setLocalSteps(stepsFromServer);
    setDirtyOrder(false);
  }, [stepsFromServer]);

  function moveStepLocal(fromOrdinal, toOrdinal) {
    setLocalSteps((arr) => {
      const a = [...arr];
      const from = fromOrdinal - 1;
      const to = toOrdinal - 1;
      const [moved] = a.splice(from, 1);
      a.splice(to, 0, moved);
      return a;
    });
    setDirtyOrder(true);
  }

  async function saveOrder() {
    if (savingOrderRef.current) return;
    savingOrderRef.current = true;
    try {
      const target = localSteps;
      const current = [...stepsFromServer];
      for (let i = 0; i < target.length; i++) {
        const wanted = target[i];
        const curIdx = current.indexOf(wanted);
        if (curIdx !== i) {
          await platformReorderSteps(id, curIdx + 1, i + 1);
          const [moved] = current.splice(curIdx, 1);
          current.splice(i, 0, moved);
        }
      }
      await load();
      setDirtyOrder(false);
    } catch (e) {
      alert(e?.message || t("platform_editor.save_order_failed"));
    } finally {
      savingOrderRef.current = false;
    }
  }

  /* Metadata save */
  async function saveMeta(e) {
    e?.preventDefault?.();
    setSavingMeta(true);
    try {
      await platformUpdateTraining(id, {
        title: meta.title,
        description: meta.description,
        slug: meta.slug,
        visibility: !!meta.visibility,
        tenantId: meta.tenantId ?? null,
        // NEW: persist flags
        ssmRequired: !!meta.ssmRequired,
        suRequired: !!meta.suRequired,
      });
      await load();
    } catch (e2) {
      alert(e2?.message || t("platform_editor.save_meta_failed"));
    } finally {
      setSavingMeta(false);
    }
  }

  /* Steps CRUD (unchanged) */
  async function appendStep() {
    if (!newStep.title || !newStep.type) {
      alert(t("platform_editor.required_type_title"));
      return;
    }
    setWorking(true);
    try {
      let payload = { type: newStep.type, title: newStep.title };
      if (newStep.type === "quiz") {
        payload.filename = newStep.filename || "step-quiz.json";
        payload.questions = newStep.questions || [];
      } else {
        if (!newStep.file) {
          alert(t("platform_editor.pick_file_for_doc_video"));
          setWorking(false);
          return;
        }
        const base64 = await fileToBase64(newStep.file);
        payload = {
          ...payload,
          filename: newStep.filename || newStep.file.name,
          contentBase64: base64,
        };
      }
      await platformAppendStep(id, payload);
      await load();
      setNewStep(emptyStep);
    } catch (e) {
      alert(e?.message || t("platform_editor.append_failed"));
    } finally {
      setWorking(false);
    }
  }

  async function insertStep() {
    const ordinal = parseInt(insertAt, 10);
    if (!ordinal || ordinal < 1) {
      alert(t("platform_editor.provide_valid_ordinal"));
      return;
    }
    if (!newStep.title || !newStep.type) {
      alert(t("platform_editor.required_type_title"));
      return;
    }
    setWorking(true);
    try {
      let payload = { type: newStep.type, title: newStep.title };
      if (newStep.type === "quiz") {
        payload.filename = newStep.filename || "step-quiz.json";
        payload.questions = newStep.questions || [];
      } else {
        if (!newStep.file) {
          alert(t("platform_editor.pick_file_for_doc_video"));
          setWorking(false);
          return;
        }
        const base64 = await fileToBase64(newStep.file);
        payload = {
          ...payload,
          filename: newStep.filename || newStep.file.name,
          contentBase64: base64,
        };
      }
      await platformInsertStep(id, ordinal, payload);
      await load();
      setNewStep(emptyStep);
      setInsertAt("");
    } catch (e) {
      alert(e?.message || t("platform_editor.insert_failed"));
    } finally {
      setWorking(false);
    }
  }

  async function removeStep(ordinal) {
    if (!window.confirm(t("platform_editor.confirm_remove_step", { n: ordinal }))) return;
    try {
      await platformRemoveStep(id, ordinal, { deleteFiles: false });
      await load();
    } catch (e) {
      alert(e?.message || t("platform_editor.remove_step_failed"));
    }
  }

  async function replaceAllSteps(filesPayload) {
    try {
      await platformReplaceContent(id, { deleteOrphans: false, steps: filesPayload });
      await load();
    } catch (e) {
      alert(e?.message || t("platform_editor.replace_failed"));
    }
  }

  /* Quiz question editor (unchanged) */
  function addQuizQuestion() {
    setNewStep((s) => ({
      ...s,
      questions: [
        ...(s.questions || []),
        { text: "", options: ["", "", ""], answerIndexes: [0] },
      ],
    }));
  }
  function updateQuizQuestion(idx, patch) {
    setNewStep((s) => {
      const qs = [...(s.questions || [])];
      const existing = qs[idx] || { options: [], answerIndexes: [] };
      let updated = { ...existing, ...patch };
      if (patch.options) {
        const optsLen = patch.options.length;
        const prevAns = existing.answerIndexes || [];
        updated.answerIndexes = (prevAns || []).filter((i) => i < optsLen);
      }
      qs[idx] = updated;
      return { ...s, questions: qs };
    });
  }

  if (!isPlatformAdmin) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Card>
          <div className="text-sm text-black/70">
            <Trans i18nKey="platform_editor.need_role">
              You need the <code>platform_admin</code> role to edit trainings.
            </Trans>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("platform_editor.page_title")}</h1>
        <Button variant="ghost" onClick={() => nav("/platform/trainings")}>
          {t("platform_editor.back_to_list")}
        </Button>
      </div>

      {/* Metadata */}
      <Card title={t("platform_editor.meta_title")}>
        {loading ? (
          <div className="text-sm text-black/60">{t("platform_editor.loading")}</div>
        ) : err ? (
          <div className="text-sm text-red-600">{err}</div>
        ) : (
          <form className="grid sm:grid-cols-2 gap-3" onSubmit={saveMeta}>
            <div>
              <label className="block text-xs text-black/60 mb-1">
                {t("platform_editor.label_title")}
              </label>
              <Input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-black/60 mb-1">
                {t("platform_editor.label_slug")}
              </label>
              <Input value={meta.slug} onChange={(e) => setMeta({ ...meta, slug: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-black/60 mb-1">
                {t("platform_editor.label_description")}
              </label>
              <Input value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} />
            </div>

            {/* Visibility */}
            <div className="flex items-center gap-2">
              <input
                id="vis"
                type="checkbox"
                checked={meta.visibility}
                onChange={(e) => setMeta({ ...meta, visibility: e.target.checked })}
              />
              <label htmlFor="vis" className="text-sm">
                {t("platform_editor.label_visible")}
              </label>
            </div>

            {/* NEW: Certificate kinds */}
            <div className="flex items-center gap-2">
              <input
                id="ssmRequired"
                type="checkbox"
                checked={meta.ssmRequired}
                onChange={(e) => setMeta({ ...meta, ssmRequired: e.target.checked })}
              />
              <label htmlFor="ssmRequired" className="text-sm">
                {t("platform_editor.label_ssm_required", "Requires SSM certificate")}
              </label>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="suRequired"
                type="checkbox"
                checked={meta.suRequired}
                onChange={(e) => setMeta({ ...meta, suRequired: e.target.checked })}
              />
              <label htmlFor="suRequired" className="text-sm">
                {t("platform_editor.label_su_required", "Requires SU certificate")}
              </label>
            </div>

            <div className="sm:col-span-2">
              <Button type="submit" disabled={savingMeta}>
                {savingMeta ? t("platform_editor.btn_saving_meta") : t("platform_editor.btn_save_meta")}
              </Button>
            </div>
          </form>
        )}
      </Card>

      {/* Steps */}
      <Card title={t("platform_editor.steps_title", { count: localSteps.length })}>
        {loading ? (
          <div className="text-sm text-black/60">{t("platform_editor.loading")}</div>
        ) : (
          <>
            <ul className="divide-y divide-black/5">
              {localSteps.map((s, idx) => {
                const ordinal = idx + 1;
                return (
                  <li key={s.id || s.src || s.contentUrl || ordinal} className="py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-xs text-black/50">#{ordinal}</div>
                        <StepBadge type={s.type} />
                        <div className="font-medium truncate">{s.title}</div>
                      </div>
                      <div className="text-xs text-black/50 mt-1">
                        {s.type === "video" && (s.src || s.filename)}
                        {s.type === "doc" && (s.contentUrl || s.filename)}
                        {s.type === "quiz" && (s.contentUrl || s.filename || "quiz.json")}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {ordinal > 1 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => moveStepLocal(ordinal, ordinal - 1)}
                          title={t("platform_editor.move_up")}
                        >
                          {t("platform_editor.move_up")}
                        </Button>
                      )}
                      {ordinal < localSteps.length && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => moveStepLocal(ordinal, ordinal + 1)}
                          title={t("platform_editor.move_down")}
                        >
                          {t("platform_editor.move_down")}
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => removeStep(ordinal)}>
                        {t("platform_editor.remove")}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>

            {dirtyOrder && (
              <div className="mt-3 flex gap-2">
                <Button onClick={saveOrder}>{t("platform_editor.save_order")}</Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setLocalSteps(stepsFromServer);
                    setDirtyOrder(false);
                  }}
                >
                  {t("platform_editor.discard")}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Add / Insert Step */}
      <Card title={t("platform_editor.add_insert_title")}>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-black/60 mb-1">{t("platform_editor.field_type")}</label>
            <select
              className="px-3 py-2 rounded-xl border border-black/10 text-sm"
              value={newStep.type}
              onChange={(e) => setNewStep({ ...newStep, type: e.target.value })}
            >
              <option value="doc">Document (PDF)</option>
              <option value="video">Video (MP4)</option>
              <option value="quiz">Quiz (JSON)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-black/60 mb-1">{t("platform_editor.field_title")}</label>
            <Input
              value={newStep.title}
              onChange={(e) => setNewStep({ ...newStep, title: e.target.value })}
              placeholder="Intro / Policy / Knowledge Check"
            />
          </div>

          {newStep.type !== "quiz" ? (
            <>
              <div>
                <label className="block text-xs text-black/60 mb-1">{t("platform_editor.file")}</label>
                <input
                  type="file"
                  accept={newStep.type === "video" ? "video/mp4" : "application/pdf"}
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setNewStep({ ...newStep, file: f, filename: f?.name || "" });
                  }}
                />
              </div>
              <div>
                <label className="block text-xs text-black/60 mb-1">{t("platform_editor.filename_optional")}</label>
                <Input
                  value={newStep.filename}
                  onChange={(e) => setNewStep({ ...newStep, filename: e.target.value })}
                  placeholder="step-001-intro.mp4 / step-002-doc.pdf"
                />
              </div>
            </>
          ) : (
            <>
              <div className="sm:col-span-2">
                <label className="block text-xs text-black/60 mb-1">{t("platform_editor.quiz_filename")}</label>
                <Input
                  value={newStep.filename}
                  onChange={(e) => setNewStep({ ...newStep, filename: e.target.value })}
                  placeholder="step-003-quiz.json"
                />
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium">{t("platform_editor.questions")}</div>
                  <Button size="sm" variant="ghost" onClick={addQuizQuestion}>
                    {t("platform_editor.add_question")}
                  </Button>
                </div>
                <div className="space-y-3">
                  {(newStep.questions || []).map((q, qi) => (
                    <div key={qi} className="p-3 rounded-xl border border-black/10">
                      <label className="block text-xs text-black/60 mb-1">
                        {t("platform_editor.question_n", { n: qi + 1 })}
                      </label>
                      <Input
                        value={q.text}
                        onChange={(e) => updateQuizQuestion(qi, { text: e.target.value })}
                        placeholder="Question text…"
                      />
                      <div className="grid sm:grid-cols-3 gap-2 mt-2">
                        {(q.options || ["", "", ""]).map((opt, oi) => (
                          <Input
                            key={oi}
                            value={opt}
                            onChange={(e) => {
                              const opts = [...(q.options || ["", "", ""])];
                              opts[oi] = e.target.value;
                              updateQuizQuestion(qi, { options: opts });
                            }}
                            placeholder={t("platform_editor.option_n", { n: oi + 1 })}
                          />
                        ))}
                      </div>
                      <div className="mt-2">
                        <label className="block text-xs text-black/60 mb-1">
                          {t("platform_editor.correct_index", "Correct answer(s)")}
                        </label>
                        <div className="flex items-center gap-3">
                          {(q.options || ["", "", ""]).map((opt, oi) => {
                            const checked = (q.answerIndexes || []).includes(oi);
                            return (
                              <label key={oi} className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => {
                                    const cur = q.answerIndexes || [];
                                    let next;
                                    if (cur.includes(oi)) next = cur.filter((x) => x !== oi);
                                    else next = [...cur, oi];
                                    updateQuizQuestion(qi, { answerIndexes: next });
                                  }}
                                />
                                <span>{t("platform_editor.option_n", { n: oi + 1 })}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="flex items-center gap-2">
            <Button onClick={appendStep} disabled={working}>
              {working ? t("platform_editor.working") : t("platform_editor.append_step")}
            </Button>
            <div className="flex items-center gap-2">
              <Input
                style={{ width: 120 }}
                placeholder={t("platform_editor.insert_at_placeholder")}
                value={insertAt}
                onChange={(e) => setInsertAt(e.target.value)}
              />
              <Button onClick={insertStep} disabled={working || !insertAt}>
                {t("platform_editor.insert")}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Replace All (advanced) */}
      <Card title={t("platform_editor.replace_all_title")}>
        <div className="text-sm text-black/60 mb-3">
          {t("platform_editor.replace_hint")}
        </div>
        <ReplaceAllForm onSubmit={replaceAllSteps} />
      </Card>

      {/* Signing Configuration (platform admin) */}
      {tdata && (
        <Card title={t("signing_config.panel_title", "Signing Configuration")}>
          <div className="text-sm text-black/60 mb-3">
            {t("signing_config.panel_hint", "Configure the signing participants and field positions for each certificate kind.")}
          </div>
          <SigningConfigPanel trainingSlug={tdata.slug || id} />
        </Card>
      )}
    </div>
  );
}

/* --- ReplaceAllForm subcomponent --- */
function ReplaceAllForm({ onSubmit }) {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]); // array of { file, type, title, filename }
  const [working, setWorking] = useState(false);

  function addFileRow() {
    setFiles((arr) => [...arr, { type: "doc", title: "", file: null, filename: "" }]);
  }

  async function handleSubmit() {
    if (!files.length) {
      alert(t("platform_editor.add_at_least_one_row"));
      return;
    }
    setWorking(true);
    try {
      const steps = [];
      for (const row of files) {
        if (!row.title || !row.type) throw new Error(t("platform_editor.row_needs_type_title"));
        if (!row.file) throw new Error(t("platform_editor.pick_file_each_row"));
        const base64 = await fileToBase64(row.file);
        steps.push({
          type: row.type,
          title: row.title,
          filename: row.filename || row.file.name,
          contentBase64: base64,
        });
      }
      await onSubmit(steps);
      setFiles([]);
    } catch (e) {
      alert(e?.message || t("platform_editor.prepare_replacement_failed"));
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {files.map((row, i) => (
          <div key={i} className="grid sm:grid-cols-4 gap-2 p-3 rounded-xl border border-black/10">
            <select
              className="px-3 py-2 rounded-xl border border-black/10 text-sm"
              value={row.type}
              onChange={(e) => {
                const v = e.target.value;
                setFiles((arr) => {
                  const copy = [...arr];
                  copy[i] = { ...copy[i], type: v };
                  return copy;
                });
              }}
            >
              <option value="doc">Document (PDF)</option>
              <option value="video">Video (MP4)</option>
            </select>
            <Input
              placeholder={t("platform_editor.label_title")}
              value={row.title}
              onChange={(e) =>
                setFiles((arr) => {
                  const copy = [...arr];
                  copy[i] = { ...copy[i], title: e.target.value };
                  return copy;
                })
              }
            />
            <input
              type="file"
              accept={row.type === "video" ? "video/mp4" : "application/pdf"}
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setFiles((arr) => {
                  const copy = [...arr];
                  copy[i] = { ...copy[i], file: f, filename: f?.name || copy[i].filename };
                  return copy;
                });
              }}
            />
            <Input
              placeholder={t("platform_editor.filename_optional")}
              value={row.filename}
              onChange={(e) =>
                setFiles((arr) => {
                  const copy = [...arr];
                  copy[i] = { ...copy[i], filename: e.target.value };
                  return copy;
                })
              }
            />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={addFileRow}>{t("platform_editor.add_file_row")}</Button>
        <Button onClick={handleSubmit} disabled={working}>
          {working ? t("platform_editor.replacing") : t("platform_editor.replace_all")}
        </Button>
      </div>
    </div>
  );
}
